export type EmbedProvider = "tableau" | "datastudio" | "superset" | "iframe";

export type SupersetEmbedConfig = {
  provider: "superset";
  supersetDomain: string;
  dashboardId: string;
};

export type ParsedEmbedConfig =
  | {
      provider: "tableau" | "datastudio" | "iframe";
      source: string;
    }
  | SupersetEmbedConfig;

const isJsonLike = (value: string) =>
  value.trim().startsWith("{") && value.trim().endsWith("}");

const parseSupersetCompactConfig = (
  value: string,
): SupersetEmbedConfig | null => {
  const payload = value.slice("superset:".length);
  const [supersetDomain, dashboardId] = payload
    .split("|")
    .map((item) => item.trim());

  if (!supersetDomain || !dashboardId) {
    return null;
  }

  return {
    provider: "superset",
    supersetDomain,
    dashboardId,
  };
};

export const parseEmbedConfig = (
  value?: string | null,
): ParsedEmbedConfig | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue.startsWith("superset:")) {
    return parseSupersetCompactConfig(normalizedValue);
  }

  if (isJsonLike(normalizedValue)) {
    try {
      const parsed = JSON.parse(normalizedValue);
      if (
        parsed?.provider === "superset" &&
        typeof parsed?.supersetDomain === "string" &&
        typeof parsed?.dashboardId === "string"
      ) {
        return {
          provider: "superset",
          supersetDomain: parsed.supersetDomain,
          dashboardId: parsed.dashboardId,
        };
      }
    } catch (_error) {
      return {
        provider: "iframe",
        source: normalizedValue,
      };
    }
  }

  if (normalizedValue.includes("tableau")) {
    return {
      provider: "tableau",
      source: normalizedValue,
    };
  }

  if (normalizedValue.includes("datastudio")) {
    return {
      provider: "datastudio",
      source: normalizedValue,
    };
  }

  return {
    provider: "iframe",
    source: normalizedValue,
  };
};

export const buildSupersetCompactConfig = (
  supersetDomain: string,
  dashboardId: string,
) => `superset:${supersetDomain}|${dashboardId}`;
